import { useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    colors,
    DocOutlineIcon,
    FileDownloadIcon
} from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import {
    TOOLTIP_PLACEMENT,
    TooltipGeneral
} from "components/DesignComponents/Tooltips";
import { downloadFile } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useOverflow } from "shared/hooks/useOverflow";
import { svgFontSize } from "shared/styles/styleFunctions";
import { body2SemiBold } from "shared/styles/typography";

import FileDeleted from "./FileDeleted";
import { useFile } from "./hooks";
import { FileProps } from "./types";

const File = ({ data, isSubtitle, customCss }: FileProps) => {
    const { gray200, gray700, gray900, textPlaceholder } = colors;

    const { t } = useTranslation();
    const { getData, handleResponse } = useApi();
    const { ref, isOverflowing } = useOverflow([data?.name]);

    const { isDownloadFileNotAllowed, downloadBtnTooltipText } = useFile();

    const [isLoading, setIsLoading] = useState(false);

    return data ? (
        <div
            css={css([
                {
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    overflowX: "hidden"
                },

                customCss
            ])}
        >
            <>
                {isSubtitle && (
                    <span
                        css={[
                            body2SemiBold,
                            { color: textPlaceholder, marginRight: "5px" }
                        ]}
                    >
                        {t("General##file")}:
                    </span>
                )}

                <div
                    css={css([
                        {
                            display: "flex",
                            alignItems: "center",
                            flex: "1",
                            overflowX: "hidden",

                            "& > svg": {
                                marginRight: "8px",
                                color: textPlaceholder,
                                ...svgFontSize(16)
                            }
                        },

                        !isSubtitle && {
                            borderRadius: "8px",
                            border: `1px solid ${gray200}`,
                            padding: data?.is_downloadable
                                ? "6px 16px"
                                : "9px 15px"
                        }
                    ])}
                >
                    {!isSubtitle && <DocOutlineIcon />}

                    <TooltipGeneral
                        title={data?.name}
                        placement={TOOLTIP_PLACEMENT.Top}
                        disableHoverListener={!isOverflowing}
                    >
                        <span
                            ref={ref}
                            css={css({
                                flex: "1",
                                color: isSubtitle ? gray900 : gray700,
                                fontSize: isSubtitle ? "14px" : "12px",
                                fontWeight: "500",
                                lineHeight: isSubtitle ? "20px" : "16px",
                                letterSpacing: isSubtitle ? "0.1px" : "0.4px",
                                whiteSpace: "nowrap",
                                overflowX: "hidden",
                                textOverflow: "ellipsis"
                            })}
                        >
                            {data?.name}
                        </span>
                    </TooltipGeneral>

                    {data?.is_downloadable && (
                        <TooltipGeneral
                            placement={TOOLTIP_PLACEMENT.Top}
                            title={downloadBtnTooltipText}
                        >
                            <span
                                css={css({
                                    marginLeft: isSubtitle ? "8px" : "16px"
                                })}
                            >
                                <Button
                                    color={BUTTON_COLORS.Secondary}
                                    size={BUTTON_SIZES.Tiny}
                                    variant={BUTTON_VARIANTS.IconOnly}
                                    icon={<FileDownloadIcon />}
                                    isLoading={isLoading}
                                    disabled={isDownloadFileNotAllowed}
                                    onClick={() =>
                                        downloadFile({
                                            id: data.id,
                                            getData,
                                            handleResponse,
                                            setLoading: () =>
                                                setIsLoading(true),
                                            removeLoading: () =>
                                                setIsLoading(false)
                                        })
                                    }
                                />
                            </span>
                        </TooltipGeneral>
                    )}
                </div>
            </>
        </div>
    ) : (
        <FileDeleted isSubtitle={isSubtitle} customCss={customCss} />
    );
};

export default File;
